import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { GenericAccountId, GenericToolId, GenericUserID } from '../models.generic';

extendZodWithOpenApi(z);

/**
 * Possible scopes for a integration (internal config version)
 */
const IntegrationScopesEnums = ['personal', 'account', 'all'] as const;
export const IntegrationScopeSchema = z
  .enum(IntegrationScopesEnums)
  .openapi({ example: 'account', description: 'The scope of the integration' });
export type IntegrationScope = z.infer<typeof IntegrationScopeSchema>;

/**
 * Possible scopes for integrations (as presented in APIs)
 */
const PublicIntegrationScopesEnums = ['personal', 'account'] as const;
export const PublicIntegrationScopeSchema = z
  .enum(PublicIntegrationScopesEnums)
  .openapi({ example: 'account', description: 'The scope of the integration' });
export type PublicIntegrationScope = z.infer<typeof PublicIntegrationScopeSchema>;

/**
 * Nango-specific schemas for authentication
 * Re-definitions of `node_modules/@nangohq/node/dist/types` types as Zod schemas
 */

/**
 * Basic API credentials schema
 */
const NangoBasicApiCredentialsSchema = z.object({
  username: z.string(),
  password: z.string(),
});

/**
 * API Key credentials schema
 */
const NangoApiKeyCredentialsSchema = z.object({
  apiKey: z.string(),
});

/**
 * OAuth2 Client credentials schema
 */
const NangoOAuth2ClientCredentialsSchema = z.object({
  client_id: z.string(),
  client_secret: z.string(),
});

/**
 * OAuth1 Client credentials schema
 * (Added for Trello, may not be standard)
 */
const NangoOAuth1ClientCredentialsSchema = z.object({
  oAuthToken: z.string(),
  oAuthTokenSecret: z.string(),
});

/**
 * OAuth credentials override schema
 */
const NangoOAuthCredentialsOverrideSchema = z.object({
  oauth_client_id_override: z.string(),
  oauth_client_secret_override: z.string(),
});

/**
 * App Store credentials schema
 */
const NangoAppStoreCredentialsSchema = z.object({
  privateKeyId: z.string(),
  issuerId: z.string(),
  privateKey: z.string(),
  scope: z.array(z.string()).optional(),
});

/**
 * TBA (Token-Based Authentication) credentials schema
 */
const NangoTBACredentialsSchema = z.object({
  token_id: z.string(),
  token_secret: z.string(),
  oauth_client_id_override: z.string().optional(),
  oauth_client_secret_override: z.string().optional(),
});

/**
 * Tableau credentials schema
 */
const NangoTableauCredentialsSchema = z.object({
  pat_name: z.string(),
  pat_secret: z.string(),
  content_url: z.string().optional(),
});

/**
 * JWT credentials schema
 */
const NangoJwtCredentialsSchema = z.object({
  privateKeyId: z.string().optional(),
  issuerId: z.string().optional(),
  privateKey: z.union([
    z.object({
      id: z.string(),
      secret: z.string(),
    }),
    z.string(),
  ]),
});

/**
 * Two-step credentials schema
 */
const NangoTwoStepCredentialsSchema = z.object({
  type: z.literal('TWO_STEP'),
});

/**
 * Signature credentials schema
 */
const NangoSignatureCredentialsSchema = z.object({
  type: z.literal('SIGNATURE'),
  username: z.string(),
  password: z.string(),
});

/**
 * Bill credentials schema
 */
const NangoBillCredentialsSchema = z.object({
  username: z.string(),
  password: z.string(),
  organization_id: z.string(),
  dev_key: z.string(),
});

/**
 * Union of all credential schemas
 */
export const NangoTokenFormatSchemas = z.union([
  NangoBasicApiCredentialsSchema,
  NangoApiKeyCredentialsSchema,
  NangoOAuth2ClientCredentialsSchema,
  NangoOAuth1ClientCredentialsSchema,
  NangoOAuthCredentialsOverrideSchema,
  NangoAppStoreCredentialsSchema,
  NangoTBACredentialsSchema,
  NangoTableauCredentialsSchema,
  NangoJwtCredentialsSchema,
  NangoTwoStepCredentialsSchema,
  NangoSignatureCredentialsSchema,
  NangoBillCredentialsSchema,
  z.string(), // For OAuth2 tokens as strings - needed for type discrimination
]);

/**
 * Nango connection configuration schema
 * This matches the ConnectionConfig interface from @nangohq/frontend
 */
export const NangoConnectionConfigParamsSchema = z.object({
  connectionId: z
    .string()
    .uuid()
    .optional()
    .describe(
      'The public.connections `id` to target when `insert_or_update_integration_connection` is true; Useful for reviving an existing connection that has been deleted from Nango'
    ),
  accountId: GenericAccountId,
  scope: PublicIntegrationScopeSchema,
  subdomain: z.string().optional(),
  toolId: GenericToolId,
  userId: GenericUserID.describe('Authenticated user; becomes managing user for this connection'),
  mode: z
    .enum(['initial_authentication', 'refresh_authentication'])
    .default('initial_authentication')
    .describe(
      'Deprecated: This field is accepted for backwards compatibility but ignored. ' +
        'We always use initial_authentication (createConnectSession). For re-auth, ' +
        'the connectionId is passed via frontend config params instead.'
    ),
});

export type NangoConnectionConfigParams = z.infer<typeof NangoConnectionConfigParamsSchema>;

export const NangoConnectionConfigSchema = z.object({
  params: NangoConnectionConfigParamsSchema,
  user_scope: z.array(z.string()).optional(),
  detectClosedAuthWindow: z.boolean().optional(), // AuthOptions
  authorization_params: z.record(z.string(), z.string().optional()).optional(),
  credentials: NangoTokenFormatSchemas.optional(),
});
export type NangoConnectionConfig = z.infer<typeof NangoConnectionConfigSchema>;
